import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from './Sidebar';
import Header from './Header';
import Footer from './Footer';
import LoadingSpinner from '../ui/LoadingSpinner';
import EnvironmentBanner from '../EnvironmentBanner';

const Layout: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Post-commit redirect on logout. Same React 19 removeChild fix as
  // Login.tsx / Register.tsx — replaces the mid-render
  // `return <Navigate to="/login" />` to avoid the portal cleanup
  // race during the auth-flip → unmount → mount sequence.
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      
      // Auto-close sidebar on mobile when resizing
      if (mobile && sidebarOpen) {
        setSidebarOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen]);

  // Handle clicks outside sidebar to close it on mobile
  useEffect(() => {
    if (!isMobile) return;
    
    const handleOutsideClick = (e: MouseEvent) => {
      const sidebar = document.querySelector('[data-sidebar]');
      const button = document.querySelector('[data-sidebar-toggle]');
      
      if (sidebar && 
          button && 
          sidebarOpen && 
          !sidebar.contains(e.target as Node) && 
          !button.contains(e.target as Node)) {
        setSidebarOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [sidebarOpen, isMobile]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // While the post-commit redirect (above) fires, render a spinner.
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col">
      <EnvironmentBanner />
      <div className="flex flex-1 flex-col bg-background md:flex-row">
        <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
        
        <div className="flex flex-1 flex-col overflow-hidden">
          <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
          
          <main className="flex-1 overflow-auto p-4 md:p-6">
            <div className="mx-auto max-w-7xl">
              <Outlet />
            </div>
          </main>
          
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default Layout;
